import {LocalStorage, LocalStorageService, StrategyCacheService} from 'ngx-webstorage';
import {throwError as observableThrowError} from 'rxjs';
import { Injectable, inject } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {HydraService} from './hydra.service';
import {Utilisateur} from '../../class/api/Utilisateur';
import {environment} from '../../../environments/environment';
import {api} from '../../api/api-dictionnary';
import {UtilisateurRole} from '../../class/api/UtilisateurRole';
import {ROLE_KEYS} from '../consts/roles';
import {TranslateService} from '@ngx-translate/core';
import {catchError, map} from 'rxjs/operators';
import {RouterHistoryService} from './router-history.service';
import {MatDialog} from '@angular/material/dialog';
import {ModalInfoComponent} from '../../shared/components/modal-info/modal-info.component';
import {ExUtilisateur} from '../../class/ex/ex-utilisateur';
import {LOCAL_STORAGE_KEEP_ON_LOGOUT_PREFIX} from "../consts/app";
import {JwtService} from './jwt.service';

@Injectable()
export class AuthentificationService {
    translate = inject(TranslateService);
    private http = inject(HttpClient);
    private router = inject(Router);
    private hydraService = inject(HydraService);
    private localStorage = inject(LocalStorageService);
    private localStorageCache = inject(StrategyCacheService);
    private _routerHistoryService = inject(RouterHistoryService);
    private dialog = inject(MatDialog);
    private jwtService = inject(JwtService);

    //Variables
    @LocalStorage() public token: string;
    @LocalStorage() public refreshToken: string;
    @LocalStorage() public dateShort = ['dd/MM/y']; // FR de base

    public displayName = '';

    LOCAL_STORAGE_LAST_ACTIVITY_KEY = 'LAST_ACTIVITY_TIME';

    // Durée de validité d'une token avant rafraichissement
    private timeToRefreshToken = 2; //minutes
    private _userConnected: Utilisateur;
    private _isLogin = false;
    private inactivityTimer;
    private secondsBeforeDeconnection = 3600;
    private inactivityCheckInterval = 1000;

    constructor() {
        setInterval(() => {
            this.refreshTokenWhenTimeUp();
        }, 10000);
    }

    get isLogin(): boolean {
        return this._isLogin;
    }

    get userConnected(): Utilisateur {
        return this._userConnected;
    }

    set isLogin(value: boolean) {
        this._isLogin = value;
    }

    set userConnected(value: Utilisateur) {
        //On ne met à jour uniquement que les clés qui sont passé en paramètres.
        if (value != null && value) {
            if (
                this._userConnected == null ||
                this._userConnected === undefined
            ) {
                if (value.locale === 'fr') {
                    this.dateShort = ['dd/MM/y'];
                } else {

                    this.dateShort = ['MM/dd/y'];
                }

                this.translate.use(value.locale);


            }

            this._userConnected = {
                ...this._userConnected,
                ...value,
            } as Utilisateur;

            this.setDisplayName();
        } else {
            this._userConnected = null;

        }
    }

    public loginV2(username: string, password: string) {
        const data = {
            username,
            password,
        };

        return this.http.post(environment.apiHost + api.login, data).pipe(
            map(response => {
                if (response['token']) {
                    this.token = response['token'];
                }
                if (response['refresh_token']) {
                    this.refreshToken = response['refresh_token'];
                }

                this.setupInactivityTimer();
                this.loadUserDataFromServer();
                return response;
            }),
            catchError(error => observableThrowError(error))
        );
    }

    public isLoggedIn(): boolean {
        const user = this.userConnected;
        return user != null && user['id'] !== undefined && user['id'] != null;
    }

    public logout(redirect: boolean = true) {
        this._routerHistoryService.reset();
        this.disconnectUser();
        window.clearTimeout(this.inactivityTimer);

        if (redirect) {
            this.router.navigate(['/connexion']);
        }
    }

    public isRole(key, redirection = false) {

        if (this.userConnected) {
            return new ExUtilisateur(this.userConnected).isRole(key);
        }

        return false;
    }

    public hasOrInheritRole(roleKey: string) {
        switch (roleKey) {
            case ROLE_KEYS.admin:
                return this.hasRoleAdmin();
            case ROLE_KEYS.datavis:
                return this.hasRoleDataVis();
            case ROLE_KEYS.president:
                return this.hasRolePresident();
            case ROLE_KEYS.expert:
                return this.hasRoleExpert();
            case ROLE_KEYS.partner:
                return this.hasRolePartner();
            default:
                return false;
        }
    }

    public isRoleForUser(key, user: Utilisateur) {
        return user.utilisateurRoles.find((item: UtilisateurRole) => item.code === key) !== undefined;
    }

    /**
     * On ne regarde pas seulement le rôle exact pour des soucis de compatibilité
     *
     * @param redirection
     */
    public isAdmin(redirection = false): boolean {
        return this.isRole(ROLE_KEYS.admin, redirection) || this.isSuperAdmin();
    }

    public hasRoleAdmin(): boolean {
        return this.isAdmin();
    }

    public isSuperAdmin(redirection = false): boolean {
        return this.isRole(ROLE_KEYS.superadmin, redirection);
    }

    public isExpert(redirection = false): boolean {
        return this.isRole(ROLE_KEYS.expert, redirection);
    }

    public hasRoleExpert(): boolean {
        return this.isExpert()
            || this.isPresident()
            || this.isPatient()
            || this.isAssociate()
            || this.isAdmin()
            || this.isDataVis();
    }

    public isPartner(redirection = false): boolean {
        return this.isRole(ROLE_KEYS.partner, redirection);
    }

    public hasRolePartner(): boolean {
        return this.isPartner();
    }


    public isDataVis(redirection = false): boolean {
        return this.isRole(ROLE_KEYS.datavis, redirection);
    }

    public hasRoleDataVis(): boolean {
        return this.isDataVis() || this.isAdmin();
    }

    public isPresident(redirection = false): boolean {
        return this.isRole(ROLE_KEYS.president, redirection);
    }

    isPatient(redirection = false) {
        return this.isRole(ROLE_KEYS.patient, redirection);
    }

    public hasRolePresident(): boolean {
        return this.isPresident() || this.isAdmin();
    }

    public isCandidat(redirection = false): boolean {
        return true;
    }

    isAssociate(redirection = false) {
        return this.isRole(ROLE_KEYS.associate, redirection);
    }

    /**
     * Initialise le service en recherchant soit le token JWT, soit son refresh token
     * Si la session a expiré, on déconnecte tout.
     */
    public wakeUpSession() {

        if (this._hasBeenInactive()) {
            this.disconnectUser();
            this.router.navigate(['/connexion']);
            return;
        }

        this.setupInactivityTimer();

        if (this.token && !this.jwtService.isTokenExpired(this.token)) {
            this.loadUserDataFromServer();
            return;
        }

        if (this.refreshToken) {
            this.hydraService.post('/api/token/refresh', {refresh_token: this.refreshToken}).subscribe(
                res => {
                    this.token = res['token'];
                    this.refreshToken = res['refresh_token'];
                    this.loadUserDataFromServer();
                },
                err => {
                    this.disconnectUser();
                    this.router.navigate(['/']);
                }
            );
            return;
        }

        this.disconnectUser();
    }

    /**
     * @todo: ce n'est pas le rôle d'un service d'authentification
     * @param locale
     */
    public setLangueForUser(locale) {
        window.localStorage.setItem('langue', locale);
        if (this.userConnected) {
            this.localStorage.clear('/api/countries');
            const data = {
                id: this.userConnected.id,
                '@id': this.userConnected['@id'],
                locale,
            };

            this.translate.use(locale);
            this.hydraService
                .save(api.utilisateur + '/' + data.id, data)
                .subscribe(res => {
                    this.userConnected.locale = locale;
                    this.userConnected = {
                        ...this.userConnected,
                    } as Utilisateur;
                    setTimeout(() => {
                        location.reload();
                    }, 100);
                });
        }
    }

    /**
     * @todo: ce n'est pas le rôle d'un service d'authentification
     */
    public getLangueForUser() {
        const user = this.userConnected;
        if (user) {
            return user['locale'];
        } else {
            let lang = navigator.language.split('-')[0];
            if (lang !== 'fr') {
                lang = 'en';
            }
            return lang;
        }
    }

    private connectUser(user: Utilisateur) {
        this.isLogin = true;
        this.userConnected = user;

        if (this.isProfileIncomplete()) {
            this._routerHistoryService.navTo('/connexion/completer-mon-profil');
            return;
        }

        const previousRoute = window.history.state.previousRoute;
        if (!previousRoute) {
            this.router.navigate(['/']);
        } else {
            this.router.navigateByUrl(
                previousRoute.url, {
                    state: {
                        previousRoute: null
                    }
                }
            );
        }
    }

    private disconnectUser() {
        this.isLogin = false;
        this.userConnected = null;
        this.refreshToken = null;
        this.token = null;

        for (var i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);

            if (!key.startsWith(LOCAL_STORAGE_KEEP_ON_LOGOUT_PREFIX)) {
                window.localStorage.removeItem(key);
            }
        }

        // clear le cache de ngx-webstorage, sinon cet idiot retrouve les clés supprimées
        this.localStorageCache.clear(this.localStorage.getStrategyName());
    }

    private loadUserDataFromServer() {
        this.hydraService.getItem(api.utilisateur + '/self')
            .subscribe(
                (res: Utilisateur) => {
                    if (
                        res['laboratoire'] != null &&
                        typeof res['laboratoire'] === 'string'
                    ) {
                        this.hydraService
                            .getItem(res.laboratoire)
                            .subscribe(res2 => {
                                res['laboratoire'] = res2 as any;
                                this.connectUser(res);
                            });
                    } else {
                        this.connectUser(res);
                    }
                },
                err => {
                    this.disconnectUser();
                }
            );
    }

    protected setDisplayName() {
        this.displayName = this.userConnected.nom.toUpperCase() + ' ' + this.userConnected.prenom;
        if (this.displayName.length > 24) {
            this.displayName = this.displayName.substr(0, 24) + '.';
        }
    }

    private isProfileIncomplete(): boolean {
        return (
            !this.isAdmin() &&
            this.userConnected['inscriptionComplete'] === false
        );
    }

    private logoutForInactivity() {
        this.logout();
        window.clearInterval(this.inactivityTimer);

        const modal = this.dialog.open(ModalInfoComponent, {
            data: {content: `You have been logged out for inactivity. The maximum duration of inactivity authorized is 1 hour`},
            panelClass: 'arc-dialog-panel', backdropClass: 'arc-dialog-backdrop'
        });

        const sub = modal.afterClosed().subscribe(
            res => {
                sub.unsubscribe();
            }
        );
    }

    private refreshTokenWhenTimeUp() {
        if (this.token && this.isLogin && this.refreshToken) {
            const validTokenMinutes =
                (this.jwtService.getTokenExpirationDate(this.token).getTime() -
                    new Date().getTime()) /
                1000 /
                60;

            if (validTokenMinutes <= this.timeToRefreshToken) {
                this.hydraService
                    .post('/api/token/refresh?noLoader=true', {
                        refresh_token: this.refreshToken,
                    })
                    .subscribe(
                        res => {
                            this.token = res['token'];
                            this.refreshToken = res['refresh_token'];
                        },
                        err => {
                            this.disconnectUser();
                        }
                    );
            }
        }
    }

    private resetInactivityTimer() {
        const now = Date.now(); /* Milliseconds since 1970/01/01. */
        this.localStorage.store(this.LOCAL_STORAGE_LAST_ACTIVITY_KEY, now);
    }

    private setupInactivityTimer() {
        if (this.inactivityTimer) {
            window.clearInterval(this.inactivityTimer);
        } else {
            document.addEventListener('mousemove', () => this.resetInactivityTimer());
            document.addEventListener('mousedown', () => this.resetInactivityTimer());
            document.addEventListener('keyup', () => this.resetInactivityTimer());
            document.addEventListener('touchmove', () => this.resetInactivityTimer());
        }

        this.resetInactivityTimer();
        this.checkInactivity();
    }

    private checkInactivity(): void {
        setTimeout(() => {
            this.checkInactivity();
        }, this.inactivityCheckInterval);


        if (!this.userConnected) {
            return;
        }

        if (this._hasBeenInactive()) {
            this.logoutForInactivity();
        }


    }

    private _hasBeenInactive(): boolean {
        const last_activity = this.localStorage.retrieve(this.LOCAL_STORAGE_LAST_ACTIVITY_KEY);
        if (!last_activity) {
            this.resetInactivityTimer();
            return false;
        }

        const timerExpiredAt = new Date(
            last_activity + (this.secondsBeforeDeconnection * 1000)
        );

        return (new Date() > timerExpiredAt);
    }

}
